// src/components/RiceSalesAdminDialog.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import imageCompression from "browser-image-compression";

import { db } from "../api/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

/* 定数 */
const PREFS = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];
const TIME_OPTS = Array.from({ length: 48 }).map((_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

const compressOptions = {
  maxSizeMB: 0.1, // ≈100KB目標
  maxWidthOrHeight: 1280,
  useWebWorker: true,
  maxIteration: 12,
  initialQuality: 0.7,
};

const RiceSalesAdminDialog = ({ open, onClose, onSaved }) => {
  const storage = getStorage();
  const [saving, setSaving] = useState(false);

  // 基本
  const [farmName, setFarmName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [prefecture, setPrefecture] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [productionDetail, setProductionDetail] = useState("");

  // Web/SNS
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [xUrl, setXUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");

  // 連絡先
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // 商品
  const [riceVariety, setRiceVariety] = useState("");
  const [amountKg, setAmountKg] = useState("");
  const [priceYen, setPriceYen] = useState("");
  const [shippingYen, setShippingYen] = useState("");

  // 時間
  const [contactStart, setContactStart] = useState("");
  const [contactEnd, setContactEnd] = useState("");

  // 説明
  const [cultivationNotes, setCultivationNotes] = useState("");
  const [riceFeatures, setRiceFeatures] = useState("");
  const [thoughts, setThoughts] = useState("");
  const [messageToKomebu, setMessageToKomebu] = useState("");

  // 状態
  const [isSoldOut, setIsSoldOut] = useState(false);

  // 画像
  const [riceImages, setRiceImages] = useState([]); // [{file,name,url}]
  const [farmerImages, setFarmerImages] = useState([]);

  // バリデーション
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [shippingError, setShippingError] = useState("");

  const reset = () => {
    setFarmName("");
    setContactPerson("");
    setPrefecture("");
    setMunicipality("");
    setProductionDetail("");
    setWebsiteUrl("");
    setFacebookUrl("");
    setXUrl("");
    setInstagramUrl("");
    setEmail("");
    setPhone("");
    setRiceVariety("");
    setAmountKg("");
    setPriceYen("");
    setShippingYen("");
    setContactStart("");
    setContactEnd("");
    setCultivationNotes("");
    setRiceFeatures("");
    setThoughts("");
    setMessageToKomebu("");
    setIsSoldOut(false);
    setRiceImages([]);
    setFarmerImages([]);
    setEmailError("");
    setPhoneError("");
    setShippingError("");
  };

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isDigits = (v) => /^\d+$/.test(v);

  const handleEmailChange = (e) => {
    const v = e.target.value.trim();
    setEmail(v);
    setEmailError(
      v && !isValidEmail(v) ? "xxxxxxx@yyyyyyy という形式にしてください" : ""
    );
  };
  const handlePhoneChange = (e) => {
    const v = e.target.value.replace(/\s+/g, "");
    setPhone(v);
    setPhoneError(
      v && !isDigits(v) ? "数字のみで入力してください（ハイフンなし）" : ""
    );
  };
  const handleShippingChange = (e) => {
    const v = e.target.value.trim();
    setShippingYen(v);
    setShippingError(
      v !== "" && isNaN(Number(v)) ? "数字で入力してください" : ""
    );
  };

  const handlePickImages = async (files, setList) => {
    const arr = await Promise.all(
      Array.from(files).map(async (f) => {
        const compressed = await imageCompression(f, compressOptions);
        const url = URL.createObjectURL(compressed);
        const name = f.name.replace(/\s+/g, "_");
        return { file: compressed, name, url };
      })
    );
    setList((prev) => [...prev, ...arr]);
  };

  const removeImage = (index, setList) =>
    setList((prev) => prev.filter((_, i) => i !== index));

  const onDragEnd = (result, list, setList) => {
    if (!result.destination) return;
    const arr = Array.from(list);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setList(arr);
  };

  const uploadOrderedImages = async (basePath, list) => {
    const urls = [];
    for (let i = 0; i < list.length; i++) {
      const it = list[i];
      const ref = storageRef(
        storage,
        `${basePath}/${Date.now()}_${i}_${it.name}`
      );
      await uploadBytes(ref, it.file);
      const url = await getDownloadURL(ref);
      urls.push(url);
    }
    return urls;
  };

  const handleSave = async () => {
    if (!farmName || !prefecture || !riceVariety || !priceYen) {
      alert("必須項目（農家名/都道府県/お米の種類/価格）を入力してください。");
      return;
    }
    if (email && !isValidEmail(email)) {
      setEmailError("xxxxxxx@yyyyyyy という形式にしてください");
      return;
    }
    if (phone && !isDigits(phone)) {
      setPhoneError("数字のみで入力してください（ハイフンなし）");
      return;
    }
    if (shippingYen !== "" && isNaN(Number(shippingYen))) {
      setShippingError("数字で入力してください");
      return;
    }
    if ((contactStart && !contactEnd) || (!contactStart && contactEnd)) {
      alert("連絡可能時間は開始と終了を両方選択してください。");
      return;
    }

    setSaving(true);
    try {
      const riceUrls = await uploadOrderedImages(
        "riceSales/images/rice",
        riceImages
      );
      const farmerUrls = await uploadOrderedImages(
        "riceSales/images/farmer",
        farmerImages
      );

      await addDoc(collection(db, "riceSales"), {
        farmName,
        contactPerson,
        prefecture,
        municipality,
        productionDetail,
        websiteUrl,
        facebookUrl,
        xUrl,
        instagramUrl,
        email,
        phone,
        riceVariety,
        amountKg: amountKg === "" ? null : Number(amountKg),
        priceYen: priceYen === "" ? null : Number(priceYen),
        shippingYen: shippingYen === "" ? null : Number(shippingYen),
        contactStart: contactStart || null,
        contactEnd: contactEnd || null,
        cultivationNotes,
        riceFeatures,
        thoughts,
        messageToKomebu,
        imageRiceUrls: riceUrls,
        imageFarmerUrls: farmerUrls,
        imageRiceUrl: riceUrls[0] || null,
        imageFarmerUrl: farmerUrls[0] || null,
        isSoldOut,
        createdAt: serverTimestamp(),
      });

      reset();
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      alert("保存に失敗しました。ネットワークや権限をご確認ください。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>お米の販売情報を登録</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* 基本 */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="農家名（屋号/田んぼ名）*"
              value={farmName}
              onChange={(e) => setFarmName(e.target.value)}
              fullWidth
            />
            <TextField
              label="ご担当者名"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label="都道府県*"
              value={prefecture}
              onChange={(e) => setPrefecture(e.target.value)}
              fullWidth
            >
              {PREFS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="市町村"
              value={municipality}
              onChange={(e) => setMunicipality(e.target.value)}
              fullWidth
            />
          </Stack>

          <TextField
            label="お米の生産地（詳細）"
            value={productionDetail}
            onChange={(e) => setProductionDetail(e.target.value)}
            fullWidth
          />

          {/* Web/SNS */}
          <TextField
            label="ウェブサイト/オンラインストア URL"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            fullWidth
          />
          {/* <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Facebook URL"
              value={facebookUrl}
              onChange={(e) => setFacebookUrl(e.target.value)}
              fullWidth
              helperText="例：https://facebook.com/yourpage または facebook.com/yourpage"
            />
            <TextField
              label="X（旧Twitter）URL"
              value={xUrl}
              onChange={(e) => setXUrl(e.target.value)}
              fullWidth
              helperText="例：https://x.com/yourname または x.com/yourname"
            />
            <TextField
              label="Instagram URL"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              fullWidth
              helperText="例：https://instagram.com/youraccount または instagram.com/youraccount"
            />
          </Stack> */}

          {/* 連絡先 */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="連絡先（メールアドレス）"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError || "例：xxxxxxx@yyyyyyy"}
              fullWidth
            />
            <TextField
              label="連絡先（電話番号・数字のみ）"
              value={phone}
              onChange={handlePhoneChange}
              error={!!phoneError}
              helperText={phoneError || "例：09012345678（ハイフンなし）"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              fullWidth
            />
          </Stack>

          {/* 商品仕様 */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="お米の種類（品種）*"
              value={riceVariety}
              onChange={(e) => setRiceVariety(e.target.value)}
              fullWidth
            />
            <TextField
              label="量（kg）"
              type="number"
              value={amountKg}
              onChange={(e) => setAmountKg(e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="販売価格（円）*"
              type="number"
              value={priceYen}
              onChange={(e) => setPriceYen(e.target.value)}
              fullWidth
            />
            <TextField
              label="送料（円）"
              value={shippingYen}
              onChange={handleShippingChange}
              error={!!shippingError}
              helperText={shippingError || ""}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              fullWidth
            />
          </Stack>

          {/* 連絡可能時間 */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label="連絡可能時間（開始）"
              value={contactStart}
              onChange={(e) => setContactStart(e.target.value)}
              fullWidth
            >
              <MenuItem value="">未設定</MenuItem>
              {TIME_OPTS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="連絡可能時間（終了）"
              value={contactEnd}
              onChange={(e) => setContactEnd(e.target.value)}
              fullWidth
            >
              <MenuItem value="">未設定</MenuItem>
              {TIME_OPTS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* 説明 */}
          <TextField
            label="栽培方法・こだわり（50〜100字推奨）"
            value={cultivationNotes}
            onChange={(e) => setCultivationNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="お米の特徴（50〜100字推奨）"
            value={riceFeatures}
            onChange={(e) => setRiceFeatures(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="米作りへの想い（100〜200字推奨）"
            value={thoughts}
            onChange={(e) => setThoughts(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="セカイムラ/米部へのメッセージ（50〜100字推奨）"
            value={messageToKomebu}
            onChange={(e) => setMessageToKomebu(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          {/* 画像：お米 */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
            お米の写真（複数可・圧縮保存 ≈100KB）
          </Typography>
          <Button component="label" variant="outlined">
            画像を選択
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(e) =>
                e.target.files &&
                handlePickImages(e.target.files, setRiceImages)
              }
            />
          </Button>
          <DragDropContext
            onDragEnd={(r) => onDragEnd(r, riceImages, setRiceImages)}
          >
            <Droppable droppableId="riceImgs" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: "flex",
                    gap: 8,
                    overflowX: "auto",
                    padding: "4px 0",
                  }}
                >
                  {riceImages.map((it, idx) => (
                    <Draggable
                      draggableId={`rice-${idx}`}
                      index={idx}
                      key={`rice-${idx}`}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          style={{ ...prov.draggableProps.style }}
                        >
                          <ImageList
                            cols={1}
                            rowHeight={120}
                            sx={{ width: 140 }}
                          >
                            <ImageListItem>
                              <img
                                src={it.url}
                                alt={`rice-${idx}`}
                                style={{
                                  width: 140,
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                              />
                              <ImageListItemBar
                                position="top"
                                actionIcon={
                                  <span {...prov.dragHandleProps}>
                                    <DragIndicatorIcon sx={{ color: "#fff" }} />
                                  </span>
                                }
                                actionPosition="left"
                              />
                              <ImageListItemBar
                                position="bottom"
                                actionIcon={
                                  <IconButton
                                    onClick={() =>
                                      removeImage(idx, setRiceImages)
                                    }
                                    sx={{ color: "#fff" }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                }
                              />
                            </ImageListItem>
                          </ImageList>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* 画像：農家さん */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
            農家さんの写真（複数可・圧縮保存 ≈100KB）
          </Typography>
          <Button component="label" variant="outlined">
            画像を選択
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(e) =>
                e.target.files &&
                handlePickImages(e.target.files, setFarmerImages)
              }
            />
          </Button>
          <DragDropContext
            onDragEnd={(r) => onDragEnd(r, farmerImages, setFarmerImages)}
          >
            <Droppable droppableId="farmerImgs" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: "flex",
                    gap: 8,
                    overflowX: "auto",
                    padding: "4px 0",
                  }}
                >
                  {farmerImages.map((it, idx) => (
                    <Draggable
                      draggableId={`farmer-${idx}`}
                      index={idx}
                      key={`farmer-${idx}`}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          style={{ ...prov.draggableProps.style }}
                        >
                          <ImageList
                            cols={1}
                            rowHeight={120}
                            sx={{ width: 140 }}
                          >
                            <ImageListItem>
                              <img
                                src={it.url}
                                alt={`farmer-${idx}`}
                                style={{
                                  width: 140,
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                              />
                              <ImageListItemBar
                                position="top"
                                actionIcon={
                                  <span {...prov.dragHandleProps}>
                                    <DragIndicatorIcon sx={{ color: "#fff" }} />
                                  </span>
                                }
                                actionPosition="left"
                              />
                              <ImageListItemBar
                                position="bottom"
                                actionIcon={
                                  <IconButton
                                    onClick={() =>
                                      removeImage(idx, setFarmerImages)
                                    }
                                    sx={{ color: "#fff" }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                }
                              />
                            </ImageListItem>
                          </ImageList>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* ステータス */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={isSoldOut}
                onChange={(e) => setIsSoldOut(e.target.checked)}
              />
            }
            label="売り切れ"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          登録する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RiceSalesAdminDialog;
